import * as React from 'react';
import type { JSX as ReactJSX } from 'react';
import type { LucideProps } from 'lucide-react';
import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

const headingVariants = cva('scroll-m-20 text-foreground', {
  variants: {
    level: {
      1: 'text-3xl font-bold tracking-tight text-balance',
      2: 'text-2xl font-semibold leading-tight tracking-tight',
      3: 'text-xl font-semibold leading-tight',
      4: 'text-lg font-medium leading-snug',
      5: 'text-base font-medium leading-snug',
      6: 'text-base font-medium uppercase tracking-wide',
    },
    font: {
      default: '',
      display: 'font-display font-bold',
    },
  },
  defaultVariants: {
    level: 1,
    font: 'default',
  },
});

const clampLevel = (level: HeadingLevel): HeadingLevel => {
  if (level < 1) {
    return 1;
  }

  if (level > 6) {
    return 6;
  }

  return level;
};

const headingTagMap = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
} as const satisfies Record<HeadingLevel, keyof ReactJSX.IntrinsicElements>;

type IconComponent = React.ComponentType<LucideProps>;
type IconLike = IconComponent | React.ReactElement<LucideProps>;

export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  level?: HeadingLevel;
  font?: 'default' | 'display';
  icon?: IconLike;
  iconClassName?: string;
  description?: React.ReactNode;
  descriptionClassName?: string;
  wrapperClassName?: string;
  align?: 'left' | 'center';
  children?: React.ReactNode;
}

const FORWARD_REF_SYMBOL = Symbol.for('react.forward_ref');

const renderIcon = (icon: HeadingProps['icon'], iconClassName?: string): React.ReactNode => {
  if (!icon) {
    return null;
  }

  const baseClasses = cn('h-5 w-5 text-primary', iconClassName);

  if (React.isValidElement(icon)) {
    const element = icon as React.ReactElement<{
      className?: string;
      'aria-hidden'?: boolean;
    }>;

    return React.cloneElement(element, {
      className: cn(baseClasses, element.props.className),
      'aria-hidden': element.props['aria-hidden'] ?? true,
    });
  }

  const isForwardRefComponent =
    typeof icon === 'object' &&
    icon !== null &&
    // @ts-expect-error -- runtime check for forwardRef components
    icon.$$typeof === FORWARD_REF_SYMBOL;

  if (typeof icon === 'function' || isForwardRefComponent) {
    const IconComponent = icon as IconComponent;
    return <IconComponent aria-hidden className={baseClasses} />;
  }

  return icon;
};

const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  (
    {
      className,
      level = 1,
      font = 'default',
      icon,
      iconClassName,
      description,
      descriptionClassName,
      wrapperClassName,
      align = 'left',
      children,
      ...props
    },
    ref,
  ) => {
    const resolvedLevel = clampLevel(level);
    const Tag = headingTagMap[resolvedLevel] ?? 'h1';
    const hasIcon = Boolean(icon);
    const headingClasses = cn(
      headingVariants({ level: resolvedLevel, font }),
      hasIcon && 'flex items-center gap-2',
      align === 'center' && 'justify-center text-center',
      className,
    );
    const iconNode = renderIcon(icon, iconClassName);
    const label = hasIcon ? (
      <span className={align === 'center' ? 'block text-center' : 'block'}>{children}</span>
    ) : (
      children
    );
    const headingElement = React.createElement(
      Tag,
      { ref, className: headingClasses, ...props },
      iconNode,
      label,
    );
    const shouldWrap = Boolean(description) || Boolean(wrapperClassName);

    if (!shouldWrap) {
      return headingElement;
    }

    return (
      <div className={cn('space-y-2', align === 'center' && 'text-center', wrapperClassName)}>
        {headingElement}
        {description ? (
          <p
            className={cn(
              'text-sm text-muted-foreground',
              align === 'center' && 'mx-auto',
              descriptionClassName,
            )}
          >
            {description}
          </p>
        ) : null}
      </div>
    );
  },
);

Heading.displayName = 'Heading';

export { Heading, headingVariants, type HeadingLevel };
